import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Descriptions, Alert, Button, Space, Spin, message } from 'antd';
import { EyeOutlined, MailOutlined } from '@ant-design/icons';
import axios from 'axios';
import authService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const formatNZD = (n) => `NZD $${Number(n || 0).toFixed(2)}`;

/**
 * 自愿退团 + 签发 Voucher 弹窗
 * Props:
 *   open, onClose, tourist, tour（团信息），onSuccess
 */
export default function VoucherWithdrawalModal({ open, tourist, tour, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    if (!open) {
      setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return ''; });
      return;
    }
    // 默认填销售邮箱
    const defaultEmail = tourist?.salesperson?.email || '';
    form.setFieldsValue({
      sentToEmail: defaultEmail,
      ccEmail: ''
    });
    // 拉解析后的模板（展示有效期/原因 + 名称）
    if (tourist?.id) {
      axios.get(`${API_BASE}/vouchers/resolve-template`, {
        params: { touristId: tourist.id },
        headers: authService.getAuthHeaders()
      })
        .then((res) => setTemplate(res.data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tourist?.id]);

  const idNumber = tourist?.passportNumber || tourist?.documentNumber || '';
  const holderName = tourist?.passportName || tourist?.touristName || '';
  const productName = tour?.destination?.name || tour?.productName || '-';

  const validateBeforeAction = () => {
    if (!idNumber) {
      message.warning('该游客缺少证件号，无法签发 Voucher');
      return false;
    }
    return true;
  };

  const handlePreview = async () => {
    if (!validateBeforeAction()) return;
    try {
      setPreviewing(true);
      const res = await axios.post(
        `${API_BASE}/vouchers/preview`,
        { touristId: tourist.id },
        { headers: authService.getAuthHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      window.open(url, '_blank');
    } catch (err) {
      message.error('预览失败：' + (err.response?.data?.error || err.message));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateBeforeAction()) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await axios.post(
        `${API_BASE}/vouchers/withdraw`,
        {
          touristId: tourist.id,
          sentToEmail: values.sentToEmail,
          ccEmail: values.ccEmail || undefined
        },
        { headers: authService.getAuthHeaders() }
      );
      const isNew = res.data?.isNew;
      const emailOk = res.data?.email?.success;
      if (emailOk) {
        message.success(isNew ? '已签发并发送 Voucher' : 'Voucher 已重发');
      } else {
        message.warning('Voucher 已签发，但邮件发送失败：' + (res.data?.email?.error || '未知错误'));
      }
      onSuccess && onSuccess(res.data?.voucher);
      onClose && onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('提交失败：' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="自愿退团 · 签发 Voucher"
      open={open}
      onCancel={onClose}
      width={640}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button icon={<EyeOutlined />} loading={previewing} onClick={handlePreview}>预览 PDF</Button>
          <Button type="primary" icon={<MailOutlined />} loading={submitting} onClick={handleSubmit}>
            确认发放并发送邮件
          </Button>
        </Space>
      }
    >
      {!idNumber && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="该游客缺少证件号，无法签发 Voucher"
          description="请先在游客信息中补全护照/证件号，再回来签发。"
        />
      )}

      <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="持有人">{holderName}</Descriptions.Item>
        <Descriptions.Item label="证件号">
          {idNumber ? `${idNumber.slice(0, 1)}****${idNumber.slice(-4)}` : <span style={{ color: '#ff4d4f' }}>未填写</span>}
        </Descriptions.Item>
        <Descriptions.Item label="原产品">{productName}</Descriptions.Item>
        <Descriptions.Item label="原出团日期">
          {tour?.departureDate ? new Date(tour.departureDate).toLocaleDateString('zh-CN') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Voucher 面额">
          {template
            ? <span style={{ color: '#c53030', fontWeight: 'bold', fontSize: 16 }}>{formatNZD(template.amount)}</span>
            : '加载中...'}
          <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>（按模板配置）</span>
        </Descriptions.Item>
        <Descriptions.Item label="使用模板">
          {template
            ? <span>{template.name} <span style={{ color: '#999', fontSize: 12 }}>（{template.source === 'tour' ? '该团专属' : '全局默认'}）</span></span>
            : '加载中...'}
        </Descriptions.Item>
        <Descriptions.Item label="有效期">
          {template ? `${template.validDays} 天` : '加载中...'}
        </Descriptions.Item>
        <Descriptions.Item label="签发原因">
          <div style={{ whiteSpace: 'pre-wrap', color: '#666' }}>
            {template?.issueReason || '加载中...'}
          </div>
        </Descriptions.Item>
      </Descriptions>

      <Form form={form} layout="vertical">
        <Form.Item
          name="sentToEmail"
          label="接收邮箱"
          rules={[
            { required: true, message: '请填写接收邮箱' },
            { type: 'email', message: '邮箱格式不正确' }
          ]}
          tooltip="默认为该客人对应销售的邮箱，可以修改"
        >
          <Input placeholder="sales@worldway.co.nz" />
        </Form.Item>
        <Form.Item
          name="ccEmail"
          label="抄送邮箱（可选）"
          rules={[{ type: 'email', message: '邮箱格式不正确' }]}
        >
          <Input placeholder="可填一个抄送地址" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
